import type { ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ClientActivity } from '@kirin/api-client/contracts/user-security-posture';
import {
  getCoverageOrderRank,
  COVERAGE_LABELS,
} from '@kirin/api-client/contracts/user-security-posture';
import {
  getIdeClientDisplayLabel,
  getPlatformIcon,
} from '@/components/shared/platform';
import {
  formatFullTimestamp,
  isLoggedInUserMismatch,
  loggedInMatchChipClass,
} from './client-utils';

const LAST_ACTIVE_CELL_CLASS =
  'text-muted-foreground font-mono text-sm whitespace-nowrap min-w-[11rem]';

function sortClientsByCoverageAndActivity(
  clients: ClientActivity[]
): ClientActivity[] {
  return [...clients].sort((a, b) => {
    const orderA = getCoverageOrderRank(a.coverageStatus);
    const orderB = getCoverageOrderRank(b.coverageStatus);
    if (orderA !== orderB) return orderA - orderB;
    const timeA = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
    const timeB = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
    return timeB - timeA;
  });
}

interface ClientDetailsModalProps {
  userName: string;
  clients: ClientActivity[];
  open: boolean;
  onClose: () => void;
}

/**
 * Modal dialog showing detailed IDE client activity for a user.
 * Includes a Coverage/Support column indicating each client's monitoring status.
 */
export function ClientDetailsModal({
  userName,
  clients,
  open,
  onClose,
}: ClientDetailsModalProps): ReactElement {
  const sortedClients = sortClientsByCoverageAndActivity(clients);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            IDE Clients for{' '}
            <span className="font-semibold">{userName || 'Unknown User'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-table-border overflow-hidden mt-4">
          <Table className="text-xs">
            <TableHeader className="sticky top-0 z-20 border-b bg-muted">
              <TableRow className="!bg-transparent hover:!bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Coverage/Support</TableHead>
                <TableHead>Logged in as</TableHead>
                <TableHead className="min-w-[11rem]">Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedClients.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-6 text-muted-foreground"
                  >
                    No client activity recorded
                  </TableCell>
                </TableRow>
              ) : (
                sortedClients.map((client, index) => {
                  const Icon = getPlatformIcon(client.client);
                  const label = getIdeClientDisplayLabel(
                    client.client,
                    client.displayName
                  );
                  const loggedInMismatch = isLoggedInUserMismatch(
                    client.loggedInUserEmail,
                    client.loggedInUserMatch
                  );
                  return (
                    <TableRow
                      key={`${client.client}-${client.coverageStatus}-${index}`}
                      className="border-b hover:bg-muted/50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span>{label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {COVERAGE_LABELS[client.coverageStatus] ??
                          client.coverageStatus}
                      </TableCell>
                      <TableCell>
                        {client.loggedInUserEmail ? (
                          <span
                            className={
                              loggedInMismatch
                                ? `rounded px-1.5 py-0.5 font-medium ${loggedInMatchChipClass(client.loggedInUserMatch)}`
                                : 'text-foreground'
                            }
                          >
                            {client.loggedInUserEmail}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className={LAST_ACTIVE_CELL_CLASS}>
                        {client.lastActiveAt
                          ? formatFullTimestamp(client.lastActiveAt)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
